// src/Pages/CommitmentMaster.jsx
import { useEffect, useMemo, useState } from "react";
import { Container, Form, Button, Row, Col, Spinner, InputGroup } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Sample14 from "../Components/Logo";
import { useAuth } from "../Context/AuthContext";

// APIs
import { fetchCompnayCodesById } from "../Api/InvoiceApi"; // (spelling kept as provided)
import { fetchMerchants } from "../Api/ViewMerchantMaster";
import { fetchBanks } from "../Api/ViewBank";
import { saveCommitment } from "../Api/IncomingPo"; // <-- make sure this file exports saveCommitment

const CommitmentMaster = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const orgId = user?.organisationId ?? user?.orgId ?? user?.OrgId ?? null;
  const resolvedUserId =
    user?.userId ??
    user?.UserId ??
    user?.id ??
    Number(localStorage.getItem("userId")) ??
    localStorage.getItem("username") ??
    "0";

  const ready = useMemo(() => Boolean(orgId), [orgId]);

  // form state
  const [companyCode, setCompanyCode] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState("");
  const [commitmentType, setCommitmentType] = useState(""); // Type of Commitment
  const [vendorCode, setVendorCode] = useState(""); // holds code; we send NAME
  const [bankCode, setBankCode] = useState("");     // holds code; we send NAME
  const [startDate, setStartDate] = useState("");   // commitment start date
  const [endDate, setEndDate] = useState("");       // commitment end date (optional)

  // dropdown sources
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [loadingBanks, setLoadingBanks] = useState(false);

  const [companies, setCompanies] = useState([]); // [{code, name}]
  const [merchants, setMerchants] = useState([]); // [{code, name}]
  const [banks, setBanks] = useState([]);         // [{code, name}]

  const [saving, setSaving] = useState(false);

  // derive selected names
  const selectedVendorName = useMemo(
    () => merchants.find((m) => String(m.code) === String(vendorCode))?.name ?? "",
    [merchants, vendorCode]
  );
  const selectedBankName = useMemo(
    () => banks.find((b) => String(b.code) === String(bankCode))?.name ?? "",
    [banks, bankCode]
  );

  // load company codes by org
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;

    (async () => {
      setLoadingCompanies(true);
      try {
        const res = await fetchCompnayCodesById(orgId);
        const arr = Array.isArray(res) ? res : res?.data || [];
        const normalized = arr
          .map((r) => ({
            code: r?.companyCode ?? r?.CompanyCode ?? r?.code ?? r?.Code ?? "",
            name: r?.companyName ?? r?.CompanyName ?? r?.name ?? r?.Name ?? r?.Description ?? "",
          }))
          .filter((x) => x.code);
        if (!cancelled) {
          setCompanies(normalized);
          if (normalized.length === 0) toast.info("No company codes found for your organisation.");
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) toast.error("Failed to load company codes");
      } finally {
        if (!cancelled) setLoadingCompanies(false);
      }
    })();

    return () => { cancelled = true; };
  }, [ready, orgId]);

  // load merchants
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;

    (async () => {
      setLoadingMerchants(true);
      try {
        const res = await fetchMerchants(orgId);
        const arr = Array.isArray(res) ? res : res?.data || [];
        console.log(arr);
        const normalized = arr.map((r) => ({
          code: r.vendorCode,
          name: r.name,
        }));
        if (!cancelled) {
          setMerchants(normalized);
          if (normalized.length === 0) toast.info("No vendors found.");
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) toast.error("Failed to load vendors");
      } finally {
        if (!cancelled) setLoadingMerchants(false);
      }
    })();

    return () => { cancelled = true; };
  }, [ready, orgId]);

  // load banks
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;

    (async () => {
      setLoadingBanks(true);
      try {
        const res = await fetchBanks(orgId);
        const arr = Array.isArray(res) ? res : res?.data || [];
        const normalized = arr
          .map((r) => ({
            code: r?.code ?? r?.bankCode ?? r?.BankCode ?? "",
            name: r?.description ?? r?.bankName ?? r?.BankName ?? "",
          }))
          .filter((x) => x.code);
        if (!cancelled) {
          setBanks(normalized);
          if (normalized.length === 0) toast.info("No banks found.");
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) toast.error("Failed to load banks");
      } finally {
        if (!cancelled) setLoadingBanks(false);
      }
    })();

    return () => { cancelled = true; };
  }, [ready, orgId]);

  const resetForm = () => {
    setCompanyCode("");
    setDescription("");
    setAmount("");
    setFrequency("");
    setCommitmentType("");
    setVendorCode("");
    setBankCode("");
    setStartDate("");
    setEndDate("");
  };

  const validate = () => {
    if (!orgId) { toast.error("Organisation Id is missing."); return false; }
    if (!companyCode) { toast.error("Company code is required."); return false; }
    if (!description.trim()) { toast.error("Description is required."); return false; }

    const amt = Number(amount);
    if (!amount || Number.isNaN(amt)) { toast.error("Amount must be a number."); return false; }
    if (amt <= 0) { toast.error("Amount must be greater than 0."); return false; }

    if (!frequency) { toast.error("Frequency is required."); return false; }
    if (!commitmentType) { toast.error("Type of Commitment is required."); return false; }
    if (!vendorCode) { toast.error("Vendor is required."); return false; }
    if (!bankCode) { toast.error("Payments bank is required."); return false; }

    if (!selectedVendorName) { toast.error("Selected vendor is invalid."); return false; }
    if (!selectedBankName) { toast.error("Selected bank is invalid."); return false; }

    if (!startDate) {
      toast.error("Commitment Start Date is required.");
      return false;
    }

    if (endDate && new Date(endDate) < new Date(startDate)) {
      toast.error("End date cannot be earlier than start date.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Send vendor & bank NAMES to backend
    const payload = {
      description: description.trim(),
      amount: Number(amount),
      userId: Number(resolvedUserId),
      frequency,
      vendor: selectedVendorName,
      paymentsBank: selectedBankName,
      orgId: Number(orgId),
      companyCode: Number(companyCode),
      type: commitmentType,
      startDate, // NEW
      endDate,   // NEW (can be empty)
    };

    setSaving(true);
    try {
      await saveCommitment(payload);
      toast.success("Commitment saved successfully");
      resetForm();
    } catch (error) {
      console.error(error);
      const msg = error?.response?.data?.message ?? error?.message ?? "Some error happened";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!ready) {
    return (
      <div className="main-content expand">
        <div className="loading-overlay"><Sample14 /></div>
      </div>
    );
  }

  const freqOptions = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly", label: "Yearly" },
  ];

  const typeOptions = [
    { value: "online subscription", label: "Online Subscription" },
    { value: "monthly subscription", label: "Monthly Subscription" },
  ];

  const anyLoading = loadingCompanies || loadingMerchants || loadingBanks;

  return (
    <>
      <div className="main-content expand">
        <Container fluid className="fields-container">
          <h2 className="fields-title">Commitment Master</h2>

          <Form onSubmit={handleSubmit} className="fields-form">
            {/* Org + Company */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Organisation Id</Form.Label>
                  <Form.Control type="text" value={orgId ?? ""} disabled />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Code <span className="text-red">**</span></Form.Label>
                  <Form.Select
                    value={companyCode}
                    onChange={(e) => setCompanyCode(e.target.value)}
                    disabled={loadingCompanies}
                    required
                  >
                    <option value="">{loadingCompanies ? "Loading company codes..." : "Select Company Code"}</option>
                    {companies.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code}{c.name ? ` - ${c.name}` : ""}
                      </option>
                    ))}
                  </Form.Select>
                  {loadingCompanies && (
                    <div className="mt-2">
                      <Spinner animation="border" size="sm" /> Fetching company codes…
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Description + User */}
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Description <span className="text-red">**</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe the commitment"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>User Id</Form.Label>
                  <Form.Control type="text" value={resolvedUserId ?? ""} disabled />
                </Form.Group>
              </Col>
            </Row>

            {/* Amount + Frequency + Type */}
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Amount <span className="text-red">**</span></Form.Label>
                  <InputGroup>
                    <InputGroup.Text>₹</InputGroup.Text>
                    <Form.Control
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      name="Amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Frequency <span className="text-red">**</span></Form.Label>
                  <Form.Select
                    name="Frequency"
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    required
                  >
                    <option value="">Select Frequency</option>
                    {freqOptions.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Type of Commitment <span className="text-red">**</span></Form.Label>
                  <Form.Select
                    name="TypeOfCommitment"
                    value={commitmentType}
                    onChange={(e) => setCommitmentType(e.target.value)}
                    required
                  >
                    <option value="">Select Type</option>
                    {typeOptions.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Commitment Duration */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Commitment Start Date <span className="text-red">**</span></Form.Label>
                  <Form.Control
                    type="date"
                    name="StartDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Commitment End Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="EndDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate} // prevent selecting earlier than start date
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Vendor + Bank */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vendor <span className="text-red">**</span></Form.Label>
                  <Form.Select
                    name="Vendor"
                    value={vendorCode}
                    onChange={(e) => setVendorCode(e.target.value)}
                    disabled={loadingMerchants}
                    required
                  >
                    <option value="">{loadingMerchants ? "Loading vendors..." : "Select Vendor"}</option>
                    {merchants.map((m) => (
                      <option key={m.code} value={m.code}>
                        {m.code}{m.name ? ` - ${m.name}` : ""}
                      </option>
                    ))}
                  </Form.Select>
                  {loadingMerchants && (
                    <div className="mt-2">
                      <Spinner animation="border" size="sm" /> Fetching vendors…
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Payments Bank <span className="text-red">**</span></Form.Label>
                  <Form.Select
                    name="PaymentsBank"
                    value={bankCode}
                    onChange={(e) => setBankCode(e.target.value)}
                    disabled={loadingBanks}
                    required
                  >
                    <option value="">{loadingBanks ? "Loading banks..." : "Select Bank"}</option>
                    {banks.map((b) => (
                      <option key={b.code} value={b.code}>
                        {b.code}{b.name ? ` - ${b.name}` : ""}
                      </option>
                    ))}
                  </Form.Select>
                  {loadingBanks && (
                    <div className="mt-2">
                      <Spinner animation="border" size="sm" /> Fetching banks…
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Actions */}
            <div className="fields-btn-container">
              <Button
                variant="secondary"
                type="button"
                className="me-2"
                onClick={resetForm}
                disabled={saving || anyLoading}
              >
                Reset
              </Button>

              <Button
                variant="primary"
                type="submit"
                className="fields-submit me-2"
                disabled={saving || anyLoading}
              >
                {saving ? "Saving..." : "Save Commitment"}
              </Button>

              <Button
                variant="outline-primary"
                type="button"
                onClick={() => navigate("/viewcommitment")}
              >
                View Commitments
              </Button>
            </div>
          </Form>
        </Container>
      </div>

      {(saving || anyLoading) && (
        <div className="loading-overlay">
          <Sample14 />
        </div>
      )}
    </>
  );
};

export default CommitmentMaster;
